import { useToast } from "@/hooks/use-toast";
import { useSetAtom } from "jotai";
import Cookies from "js-cookie";
import { useTranslations } from "next-intl";
import { parseAuthError } from "./auth-errors";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import useSWR from "swr";
import { userDataAtom } from "./atoms";
import { authCookieFlags, glob, useApi } from "./pariette";
import { User } from "./types";
export const useAuth = ({ middleware, redirectIfAuthenticated }: any = {}) => {
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations("general");
  const tAuth = useTranslations("auth");
  const bearerToken = Cookies.get(glob.authData.authorizationKey) ? glob.authData.authorizationType + Cookies.get(glob.authData.authorizationKey) : "";
  const headersx = { Authorization: bearerToken };
  const { $get, $post } = useApi(true);
  const setUser = useSetAtom(userDataAtom);

  const {
    data: user,
    error,
    mutate,
  }: { data: User; error: any; mutate: () => void } = useSWR(
    glob.authData.me,
    () =>
      $get(glob.authData.me, headersx)
        .then((res) => {
          if (res.status) {
            if (res?.data?.jwt) {
              Cookies.set(glob.authData.authorizationKey, res.data.jwt, {
                expires: glob.authData.authorizationExpires,
              ...authCookieFlags(),
              });
            }
            setUser(res.data);
            return res.data;
          } else {
            if (res.code == 401) {
              if (middleware == "auth") {
                logout(true);
              }
            } else if (res.code == 403) {
              if (middleware == "auth") {
                toast({ variant: "destructive", title: t("no_authority"), description: t("no_authority_description") });
                router.push(glob.authData.loginRedirect);
              }
            }
            setUser(null);
          }
        })
        .catch((error) => {
          setUser(null);
          if (error.code == 401) {
            if (middleware == "auth") {
              logout(true);
            }
          } else if (error.code == 403) {
            if (middleware == "auth") {
              toast({ variant: "destructive", title: t("no_authority"), description: t("no_authority_description") });
              router.push(glob.authData.loginRedirect);
            }
          }
          if (error.code !== 409) throw error;
          router.push("/verify-email");
        }),
    {
      // Rolling session: me() her çağrıda yeni JWT dönüyor; arka planda
      // 5 dakikada bir ping atarak cookie TTL'i boyunca oturumu taze tutuyoruz.
      // Kullanıcı sekmesi arkaplanda bile olsa JWT zaman aşımına uğramaz.
      refreshInterval: 5 * 60 * 1000,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      // Ağ hatası veya geçici backend aksamasında tek bir 401 yüzünden
      // kullanıcıyı düşürmemek için kısa retry penceresi.
      errorRetryCount: 2,
      errorRetryInterval: 3000,
    },
  );

  const login = async ({ setErrors, setLoader, setTFA, setPollData, getSecurity, setIsPending, ...props }: any) => {
    setErrors(null);
    setLoader(true);
    const api = props.form.store_code ? `auth/aysoft-login` : `auth/login`;
    return $post(api, props.form)
      .then((res) => {
        // app_2fa: backend onay bekliyor → polling başlasın
        if (res.approval === "pending" && res.poll_token) {
          // approver === "owner": mesai dışı çalışan girişi, onayı mağaza sahibi veriyor
          setPollData({ poll_token: res.poll_token, approval: "pending", approver: res.approver });
          return;
        }
        if (res.status) {
          Cookies.set(glob.authData.authorizationKey, res.access_token, {
            expires: glob.authData.authorizationExpires,
              ...authCookieFlags(),
          });

          if (res.stores?.length > 0) {
            localStorage.setItem("storeData", JSON.stringify({ ...res.stores[0], token: res.stores[0].store }));
          }

          setErrors(null);

          const homePage = localStorage.getItem("homePage") ? `/${localStorage.getItem("homePage")}` : "";
          router.push(props.form.store_code ? homePage || glob.authData.loginRedirect : "/Auth/Redirect");
          window.setTimeout(() => setLoader(false), 1000);
          mutate();
        }
      })
      .catch((error) => {
        // 5 Eki 2026: tüm hata biçimleri tek AuthMessage'a çevrilir (kod + ref).
        // 423 kilit → notice.lock (SecurityLockPanel), 416 → kod adımı.
        const notice = parseAuthError(error, tAuth);
        if (notice.lock) {
          setErrors(notice);
          return;
        }
        getSecurity(props.form.email ?? props.form.phone);
        if (notice.needsCode) {
          setTFA(true);
          setIsPending(true);
        }
        setErrors(notice);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const forgotPassword = async ({ setErrors, setLoader, setStatus, form }: any) => {
    // setErrors([])
    // setStatus(null)
    await $post("auth/forgot-password", form)
      .then((response) => {
        if (response.status) {
          setStatus(response.status);
        } else {
          setErrors(response);
        }
      })
      .catch((error) => {
        if (error.status !== 422) {
          setErrors(error);
        }
      })
      .finally(() => {
        setTimeout(() => {
          setLoader(false);
        }, 1000);
      });
  };

  const resetPassword = async ({ setErrors, form, setLoader }: any) => {
    // setErrors([])
    // setStatus(null)
    await $post("auth/reset-password", form)
      .then((response) => {
        if (response.status) {
          router.push("/Auth/Login?status=passwordResetted");
        } else {
          setErrors(response);
        }
      })
      .catch((error) => {
        setErrors(error);
      })
      .finally(() => {
        setTimeout(() => {
          setLoader(false);
        }, 1000);
      });
  };

  const logout = async (force?: boolean) => {
    const headersl = { headers: { Authorization: bearerToken } };
    // eSIM promosyonu her yeni login'de yeniden çıksın diye "görüldü" flag'ini temizle
    // (sürüm bağımsız: bkz. ESIM_PROMO_SEEN_KEY - EsimPromoDialog.tsx).
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("esim-promo-seen")) localStorage.removeItem(key);
    });
    caches.keys().then((keyList) =>
      Promise.all(
        keyList.map((key) => {
          return caches.delete(key);
        }),
      ),
    );
    if (!error || force) {
      await $post(glob.authData.logout, {}, headersl)
        .then(() => {
          mutate();
          Cookies.remove(glob.authData.authorizationKey);
          if (window.location.pathname !== glob.authData.loginPage) {
            window.location.pathname = glob.authData.loginPage;
          }
        })
        .catch((err) => {
          Cookies.remove(glob.authData.authorizationKey);
          // localStorage.removeItem("storeData");
          if (window.location.pathname !== glob.authData.loginPage) {
            window.location.pathname = glob.authData.loginPage;
          }
        });
    }
  };

  useEffect(() => {
    const headersu = { Authorization: bearerToken };
    const signout = async () => {
      if (!error) {
        await $post(glob.authData.logout, {}, headersu).then(() => {
          mutate();
          Cookies.remove(glob.authData.authorizationKey);
          if (window.location.pathname !== "/") {
            window.location.pathname = "/";
          }
        });
      }
    };

    if (middleware === "guest" && window.location.pathname === glob.authData.resetPage) {
      return;
    }

    if (middleware === "guest" && redirectIfAuthenticated && user) {
      router.push(redirectIfAuthenticated);
    }

    if (window.location.pathname === "/verify-email" && user?.email_verified_at) {
      router.push(redirectIfAuthenticated);
    }

    if (middleware === "auth" && error) {
      signout();
    }
  }, [user, error, middleware, redirectIfAuthenticated, mutate, bearerToken, router, $post]);

  return { user, login, logout, forgotPassword, resetPassword };
};
